import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { Hero } from "@/components/site/Hero";
import { Intro } from "@/components/site/Intro";
import { AccommodationGrid } from "@/components/site/AccommodationGrid";
import { AmenitiesSection } from "@/components/site/AmenitiesSection";
import { FalconLakeFeature } from "@/components/site/FalconLakeFeature";
import { EventsFeature } from "@/components/site/EventsFeature";
import { PropertyGallery } from "@/components/site/PropertyGallery";
import { Testimonials } from "@/components/site/Testimonials";
import { ContactCTA } from "@/components/site/ContactCTA";
import { BUSINESS, CAMPGROUND_ID, FACEBOOK_URL } from "@/lib/site";
import { PHOTOS } from "@/components/site/images";

const TITLE = "Amigo Inn & RV Park | RV Camping in Zapata, TX";
const DESCRIPTION =
  "Stay near Falcon Lake at Amigo Inn & RV Park in Zapata, Texas. Enjoy full-hookup RV sites, cabins, motel rooms, tent camping, Wi-Fi, and more.";
const CANONICAL = "https://amigoinn-rvpark.com/";
const heroImage = PHOTOS.rvSite;
const OG_IMAGE = `https://amigoinn-rvpark.com${heroImage.src}`;

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      {
        rel: "preload",
        as: "image",
        href: heroImage.src,
        imageSrcSet: heroImage.srcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Campground", "LodgingBusiness"],
          "@id": CAMPGROUND_ID,
          name: BUSINESS.name,
          url: CANONICAL,
          telephone: BUSINESS.phone,
          email: BUSINESS.email,
          description: DESCRIPTION,
          image: OG_IMAGE,
          sameAs: [FACEBOOK_URL],
          address: {
            "@type": "PostalAddress",
            streetAddress: BUSINESS.street,
            addressLocality: BUSINESS.city,
            addressRegion: BUSINESS.region,
            postalCode: BUSINESS.postalCode,
            addressCountry: BUSINESS.country,
          },
          amenityFeature: [
            "Full RV Hookups",
            "30 & 50 Amp Service",
            "Wi-Fi",
            "Cable TV",
            "Onsite Laundry",
            "Off-Leash Pet Area",
            "Clubhouse",
            "Covered Picnic Area",
            "Barbecue Pits",
          ].map((name) => ({ "@type": "LocationFeatureSpecification", name, value: true })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: CANONICAL }],
        }),
      },
    ],
  }),
});

function Index() {
  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main">
        <Hero />
        <Intro />
        <AccommodationGrid />
        <AmenitiesSection />
        <FalconLakeFeature />
        <EventsFeature />
        <PropertyGallery />
        <Testimonials />
        <ContactCTA />
      </main>

      <SiteFooter />
      <MobileBookingBar />
    </div>
  );
}
